import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { MaskedLines } from "./Reveal";
import { scrollToId } from "../lib/scroll";

export default function Hero() {
  const sectionRef = useRef(null);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, -36]);
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 50]);

  return (
    <section
      ref={sectionRef}
      id="top"
      className="relative flex min-h-screen items-center overflow-hidden pt-24 pb-16"
    >
      <div
        aria-hidden
        className="absolute inset-y-0 right-0 z-0 hidden w-[52%] overflow-hidden lg:block"
      >
        <img
          src="/artwork/cover-brush.jpg"
          alt=""
          className="h-full w-full scale-105 object-cover"
        />
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#FBF8F3_0%,#FBF8F3_18%,rgba(251,248,243,0.82)_45%,rgba(251,248,243,0.28)_74%,rgba(251,248,243,0.08)_100%)]" />
        <div className="absolute inset-0 bg-gradient-to-t from-cream via-transparent to-cream/30" />
      </div>

      <div className="pointer-events-none absolute -top-32 right-[-10%] z-0 h-[42rem] w-[42rem] rounded-full bg-[radial-gradient(circle,rgba(243,236,224,0.9),transparent_65%)]" />

      <div className="relative mx-auto grid w-full max-w-[1600px] grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12 lg:gap-8 lg:px-16">
        <motion.div
          style={{ y: yText }}
          className="rounded-sm bg-[#2b211c]/95 p-8 shadow-[0_30px_70px_-30px_rgba(24,19,16,0.6)] backdrop-blur-[2px] sm:p-10 lg:col-span-7 lg:p-12"
        >
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-gild"
          >
            <span className="inline-block h-2 w-2 rounded-full bg-gild" />
            Artwith.saivi / Artwith.saloni — Visual Artists
          </motion.p>

          <h1
            data-testid="hero-title"
            className="mt-6 font-serif text-[13vw] leading-[0.95] tracking-tight text-cream sm:text-7xl lg:text-8xl xl:text-[6.5rem]"
          >
            <MaskedLines
              delay={0.25}
              lines={[
                "Turning emotions",
                <>and <em className="font-medium text-gild">memories</em></>,
                "into art.",
              ]}
            />
          </h1>

          <motion.p
            data-testid="hero-tagline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-md text-base leading-relaxed text-cream/75 sm:text-lg"
          >
            Original hand-painted canvases in oil &amp; acrylic by two artists,{" "}
            <span className="font-medium text-cream">Saivi Rahul Joshi and Salonee Rahul Joshi</span> —
            portraits, nature, still life, and custom paintings made from your photographs.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-center"
          >
            <button
              data-testid="hero-cta-explore"
              onClick={() => scrollToId("collection")}
              className="group inline-flex min-h-[48px] w-full items-center justify-center gap-3 rounded-full bg-cream px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-umber transition-colors duration-300 hover:bg-gild sm:w-auto"
            >
              Explore the Collection
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
            </button>
            <button
              data-testid="hero-cta-commission"
              onClick={() => scrollToId("contact")}
              className="group inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-full border border-cream/30 px-7 py-4 font-mono text-[11px] uppercase tracking-[0.2em] text-cream transition-all duration-300 hover:border-gild hover:text-gild sm:w-auto"
            >
              Commission a Painting
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-10 hidden gap-10 font-mono text-[10px] uppercase tracking-[0.24em] text-cream/50 md:flex"
          >
            <span>Hand-painted originals</span>
            <span className="text-gild">✦</span>
            <span>Oil &amp; Acrylic on canvas</span>
            <span className="text-gild">✦</span>
            <span>Custom from photos</span>
          </motion.div>
        </motion.div>
      </div>

      <div className="absolute bottom-8 right-10 hidden flex-col items-center gap-3 md:flex">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-sand">Scroll</span>
        <span className="scroll-cue block h-10 w-px bg-umber/40" />
      </div>
    </section>
  );
}
